import type { LoaderFunctionArgs } from 'react-router';
import { redirect, useLoaderData } from 'react-router';
import { GenericError } from '../../components/Error';
import { Unauthorized } from '../../components/Error/Unauthorized';
import { integrateWithPreservedAuthorization } from '../../features/integration/additional-sign-in';
import { IntegrationAlreadyRegistered } from '../../features/integration/components/IntegrationAlreadyRegistered';
import { IntegrationComplete } from '../../features/integration/components/IntegrationComplete';
import { IntegrationGuarantorEmailConflict } from '../../features/integration/components/IntegrationGuarantorEmailConflict';
import type { IntegrationConflictCodeValue } from '../../features/integration/error-codes';
import { IntegrationConflictCode } from '../../features/integration/error-codes';
import { hasAuthSuccessParams } from '../../utils/auth/search-params';

export async function integrationSignInCallbackLoader({
  request,
}: LoaderFunctionArgs): Promise<{ status: number; returnUrl?: string; conflictCode?: IntegrationConflictCodeValue }> {
  const url = new URL(request.url);
  if (!hasAuthSuccessParams(url.searchParams)) {
    // 認証クエリがない場合は home に戻す
    throw redirect('/home');
  }

  try {
    const result = await integrateWithPreservedAuthorization();
    return result;
  } catch {
    // 統合処理中のエラー (環境起因・想定外エラー) はログアウトし、 401 に丸めて扱う
    return { status: 401 };
  } finally {
    history.replaceState(null, '', location.pathname);
  }
}

export function IntegrationSignInCallbackPage() {
  const { status, returnUrl, conflictCode } = useLoaderData<typeof integrationSignInCallbackLoader>();
  switch (status) {
    case 201: {
      // 完了画面を必ず挟み、ログイン元アプリへ戻るかどうかはユーザーの操作に委ねる
      return <IntegrationComplete returnUrl={returnUrl} />;
    }
    case 400: // 同一アカウント同士の場合、画面としては同一
    case 401:
    case 403: {
      // 追加ログインが未認証の場合、画面としては同一
      return <Unauthorized />;
    }
    // 競合の原因ごとにユーザに知らせる
    case 409: {
      if (conflictCode === IntegrationConflictCode.AccountAlreadyIntegrated) {
        return <IntegrationAlreadyRegistered />;
      }
      return <IntegrationGuarantorEmailConflict />;
    }
    default: {
      return <GenericError />;
    }
  }
}
